import { Dispatch, Fragment, SetStateAction } from 'react'
import { Tbody } from '@chakra-ui/react'
import { orderBy, reverse } from 'lodash'
import {
  AmountUnit,
  DurationUnit,
  FrequencyUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
} from '../../types/Regimen'
import DentalProcedureRow from './DentalProcedureRow'
import RegimensRow from './RegimensRow'
import { isUsageCompleted, getGroupKey } from './utils'

interface RegimensTableBodyProps {
  lists: {
    label: string
    fieldId: string
    items: { [itemId: string]: string }
    id: string
  }[]
  records: Regimen[]
  amountEnabled?: boolean
  frequencyEnabled?: boolean
  durationEnabled?: boolean
  methodEnabled?: boolean
  percentageEnabled?: boolean
  allowUpdate?: boolean
  canReactivate?: boolean
  recordLabel: string
  reportingForStart: string
  month: number
  year: number
  setEditingId: Dispatch<SetStateAction<string | undefined>>
  setEditingUsageIndex: Dispatch<SetStateAction<number | undefined>>
  setStartDate: Dispatch<SetStateAction<string>>
  setFrequency: Dispatch<SetStateAction<string>>
  setFrequencyUnit: Dispatch<SetStateAction<FrequencyUnit | undefined>>
  setAmount: Dispatch<SetStateAction<string>>
  setAmountUnit: Dispatch<SetStateAction<AmountUnit | undefined>>
  setMethod: Dispatch<SetStateAction<Method | undefined>>
  setSelectedFields: Dispatch<SetStateAction<SelectedFields>>
  setDuration: Dispatch<SetStateAction<string>>
  setDurationUnit: Dispatch<SetStateAction<DurationUnit | undefined>>
  setPercentage: Dispatch<SetStateAction<Percentage | undefined>>
  modal: { onOpen: () => void }
  setConfirmEndId: Dispatch<SetStateAction<string | undefined>>
  setConfirmDelete: Dispatch<
    SetStateAction<{ recordId: string; usageIndex: number } | undefined>
  >
  onReactivate: (recordId: string) => void
  showCompleted: boolean
}

export default function RegimensTableBody({
  lists,
  records,
  amountEnabled,
  frequencyEnabled,
  durationEnabled,
  methodEnabled,
  percentageEnabled,
  allowUpdate = true,
  canReactivate = false,
  recordLabel,
  reportingForStart,
  month,
  year,
  setEditingId,
  setEditingUsageIndex,
  setStartDate,
  setFrequency,
  setFrequencyUnit,
  setAmount,
  setAmountUnit,
  setMethod,
  setSelectedFields,
  setDuration,
  setDurationUnit,
  setPercentage,
  modal,
  setConfirmEndId,
  setConfirmDelete,
  onReactivate,
  showCompleted,
}: RegimensTableBodyProps) {
  // Convert month and year to strings for display purposes
  const monthString = month.toString()
  const yearString = year.toString()

  if (lists[0].id === 'dentalProcedures') {
    const filteredRecords = orderBy(
      // Filter to remove any records with a missing dental procedure
      records.filter((record) => {
        if (lists[0].items[record.primaryId] === undefined) return false

        /**
         * Dental procedures are all completed (past events).
         * When showCompleted is false, only show items added this month.
         * When showCompleted is true, show all dental procedures.
         */
        if (!showCompleted) {
          const lastUsage = record.usages[record.usages.length - 1]
          const startDate = lastUsage.startDate
          if (!startDate) return false

          const recordDate = new Date(startDate)
          return (
            recordDate.getMonth() + 1 === month &&
            recordDate.getFullYear() === year
          )
        }

        return true
      }),
      (record) => lists[0].items[record.primaryId],
      'asc',
    )

    return (
      <Tbody>
        {filteredRecords.map((record) => (
          <DentalProcedureRow
            key={record.id}
            record={record}
            lists={lists}
            month={monthString}
            year={yearString}
            setEditingId={setEditingId}
            setStartDate={setStartDate}
            modal={modal}
          />
        ))}
      </Tbody>
    )
  }

  // Group records by primaryId (with fallback for legacy data)
  const firstFieldId = lists[0].fieldId
  const validRecords = records.filter((record) => {
    const key = getGroupKey(record, firstFieldId)
    return lists[0].items[key] !== undefined
  })

  // Split into active (no endDate) and stopped (with endDate)
  const activeRecords = validRecords.filter((r) => r.endDate === undefined)
  const stoppedRecords = validRecords.filter((r) => r.endDate !== undefined)

  // Group active records by primaryId
  const activeGroups = new Map<string, Regimen[]>()
  for (const record of activeRecords) {
    const key = getGroupKey(record, firstFieldId)
    if (!activeGroups.has(key)) activeGroups.set(key, [])
    activeGroups.get(key)!.push(record)
  }

  // Group stopped records by primaryId
  const stoppedGroups = new Map<string, Regimen[]>()
  for (const record of stoppedRecords) {
    const key = getGroupKey(record, firstFieldId)
    if (!stoppedGroups.has(key)) stoppedGroups.set(key, [])
    stoppedGroups.get(key)!.push(record)
  }

  // Sort both sections alphabetically by supplement name
  const sortByName = (
    [keyA]: [string, Regimen[]],
    [keyB]: [string, Regimen[]],
  ) => {
    const nameA = lists[0].items[keyA] ?? keyA
    const nameB = lists[0].items[keyB] ?? keyB
    return nameA.localeCompare(nameB)
  }

  const sortedActiveGroups = Array.from(activeGroups.entries()).sort(sortByName)
  const sortedStoppedGroups = Array.from(stoppedGroups.entries()).sort(
    sortByName,
  )

  return (
    <Tbody>
      {/* Active records – alphabetical, always visible */}
      {sortedActiveGroups.map(([groupKey, groupRecords]) => {
        let firstRendered = false
        return (
          <Fragment key={groupKey}>
            {groupRecords.map((record: Regimen) => {
              const displayUsages = reverse([...record.usages])

              return (
                <Fragment key={record.id}>
                  {displayUsages.map((usage, usageIndex) => {
                    const isCompleted = isUsageCompleted(
                      usageIndex,
                      record.endDate,
                    )
                    if (!showCompleted && isCompleted) {
                      return null
                    }

                    // Show the name for each separate regimen record, while
                    // keeping historical usage rows grouped beneath it.
                    const isGroupHeader = !firstRendered || usageIndex === 0
                    firstRendered = true

                    return (
                      <RegimensRow
                        key={`${record.id}-${usageIndex}`}
                        record={record}
                        usage={usage}
                        usageIndex={usageIndex}
                        lists={lists}
                        month={monthString}
                        year={yearString}
                        displayUsages={displayUsages}
                        amountEnabled={amountEnabled}
                        durationEnabled={durationEnabled}
                        frequencyEnabled={frequencyEnabled}
                        methodEnabled={methodEnabled}
                        percentageEnabled={percentageEnabled}
                        allowUpdate={allowUpdate ?? false}
                        canReactivate={canReactivate}
                        recordLabel={recordLabel}
                        reportingForStart={reportingForStart}
                        isGroupHeader={isGroupHeader}
                        setEditingId={setEditingId}
                        setEditingUsageIndex={setEditingUsageIndex}
                        setStartDate={setStartDate}
                        setFrequency={setFrequency}
                        setFrequencyUnit={setFrequencyUnit}
                        setAmount={setAmount}
                        setAmountUnit={setAmountUnit}
                        setMethod={setMethod}
                        setSelectedFields={setSelectedFields}
                        setDuration={setDuration}
                        setDurationUnit={setDurationUnit}
                        setPercentage={setPercentage}
                        modal={modal}
                        setConfirmEndId={setConfirmEndId}
                        setConfirmDelete={setConfirmDelete}
                        onReactivate={onReactivate}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </Fragment>
        )
      })}

      {/* Stopped records – alphabetical, only when showCompleted is true */}
      {showCompleted &&
        sortedStoppedGroups.map(([groupKey, groupRecords]) => {
          let firstRendered = false
          return (
            <Fragment key={`stopped-${groupKey}`}>
              {groupRecords.map((record: Regimen) => {
                const displayUsages = reverse([...record.usages])

                return (
                  <Fragment key={record.id}>
                    {displayUsages.map((usage, usageIndex) => {
                      // Show the name for each separate regimen record, while
                      // keeping historical usage rows grouped beneath it.
                      const isGroupHeader = !firstRendered || usageIndex === 0
                      firstRendered = true

                      return (
                        <RegimensRow
                          key={`${record.id}-${usageIndex}`}
                          record={record}
                          usage={usage}
                          usageIndex={usageIndex}
                          lists={lists}
                          month={monthString}
                          year={yearString}
                          displayUsages={displayUsages}
                          amountEnabled={amountEnabled}
                          durationEnabled={durationEnabled}
                          frequencyEnabled={frequencyEnabled}
                          methodEnabled={methodEnabled}
                          percentageEnabled={percentageEnabled}
                          allowUpdate={allowUpdate ?? false}
                          canReactivate={canReactivate}
                          recordLabel={recordLabel}
                          reportingForStart={reportingForStart}
                          isGroupHeader={isGroupHeader}
                          setEditingId={setEditingId}
                          setEditingUsageIndex={setEditingUsageIndex}
                          setStartDate={setStartDate}
                          setFrequency={setFrequency}
                          setFrequencyUnit={setFrequencyUnit}
                          setAmount={setAmount}
                          setAmountUnit={setAmountUnit}
                          setMethod={setMethod}
                          setSelectedFields={setSelectedFields}
                          setDuration={setDuration}
                          setDurationUnit={setDurationUnit}
                          setPercentage={setPercentage}
                          modal={modal}
                          setConfirmEndId={setConfirmEndId}
                          setConfirmDelete={setConfirmDelete}
                          onReactivate={onReactivate}
                        />
                      )
                    })}
                  </Fragment>
                )
              })}
            </Fragment>
          )
        })}
    </Tbody>
  )
}
